// Per-annotation overlay views (text boxes, markup quads, ink, shapes,
// notes, redactions, stamps, links) plus the shared pointer-drag helper.
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type React from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { dashArray, pointsToPath } from "../lib/annot-geometry.ts";
import { wavyBg } from "../lib/color-utils.ts";
import {
  NOTE_SIZE,
  quadBounds,
  shapeHeads,
  type HeadStyle,
  type InkAnnotation,
  type LinkAnnotation,
  type MarkupKind,
  type NoteAnnotation,
  type Quad,
  type RedactAnnotation,
  type ShapeAnnotation,
  type StampAnnotation,
  type TextAnnotation,
} from "../lib/protocol.ts";
import type { DragPhase, Tool } from "../lib/ui-types.ts";
import { useCoarsePointer } from "../lib/use-coarse-pointer.ts";
import { HtmlBoxDims, SvgBoxDims, SvgLineDim } from "./dim-labels.tsx";

// ─────────────────────────── text annotation ───────────────────────────

export interface TextAnnotViewProps {
  annot: TextAnnotation;
  scale: number;
  /** CSS font-family for the annot's typeface (undefined = the default
   *  Helvetica stack from the stylesheet). */
  fontCss?: string;
  tool: Tool;
  selected: boolean;
  editing: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onStartEdit: (id: string) => void;
  onCommitText: (id: string, value: string, width: number, height: number) => void;
  /** The live text on every keystroke, so the model never lags the box.
   *  `onCommitText` still ends the edit (and writes the history entry);
   *  this only keeps the value truthful while it is open. */
  onInputText: (id: string, value: string, width: number, height: number) => void;
  onMove: (id: string, x: number, y: number, phase: DragPhase) => void;
  onMeasure: (id: string, width: number, height: number) => void;
}

export const TextAnnotView = memo(function TextAnnotView({
  annot,
  scale,
  fontCss,
  tool,
  selected,
  editing,
  onSelect,
  onStartEdit,
  onCommitText,
  onInputText,
  onMove,
  onMeasure,
}: TextAnnotViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  // `wasSelected` has to be sampled at press time: the press itself
  // selects, so by pointer-up the prop always reads true and every first
  // tap would open the editor.
  const drag = useRef<{
    startX: number;
    startY: number;
    ox: number;
    oy: number;
    wasSelected: boolean;
  } | null>(null);

  // Focus + caret-to-end when entering edit mode.
  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editing]);

  // Keep the model's box size in sync with the rendered text so the
  // exported FreeText rect matches what the user sees.
  useLayoutEffect(() => {
    if (editing || !ref.current) return;
    const w = ref.current.offsetWidth / scale;
    const h = ref.current.offsetHeight / scale;
    if (Math.abs(w - annot.width) > 0.5 || Math.abs(h - annot.height) > 0.5) {
      onMeasure(annot.id, w, h);
    }
  }, [editing, annot.id, annot.value, annot.size, annot.width, annot.height, scale, onMeasure]);

  const commit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    onCommitText(
      annot.id,
      el.innerText.replace(/\n$/, ""),
      el.offsetWidth / scale,
      el.offsetHeight / scale
    );
  }, [annot.id, onCommitText, scale]);

  // Every keystroke goes straight into the model. Reporting only on blur
  // (as this did) leaves `value` at "" for as long as the box is open —
  // and BOTH things that read annotations behind the user's back take
  // that at face value: the session autosave stores the empty box, and
  // an export drops it (applyAnnotation refuses a blank FreeText). So
  // typing and then refreshing, or typing and then downloading without
  // clicking away first, silently lost the text.
  const report = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    onInputText(
      annot.id,
      el.innerText.replace(/\n$/, ""),
      el.offsetWidth / scale,
      el.offsetHeight / scale
    );
  }, [annot.id, onInputText, scale]);

  // While the box is open the DOM owns its text: re-rendering the
  // children under the caret would collapse the selection to the start
  // of the box on every keystroke. So the JSX children are frozen at the
  // value the edit began with, and `report` carries the live text out.
  const editSeed = useRef(annot.value);
  if (!editing) editSeed.current = annot.value;

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (editing) {
        e.stopPropagation();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      onSelect(annot.id, e.shiftKey);
      drag.current = {
        startX: e.clientX,
        startY: e.clientY,
        ox: annot.x,
        oy: annot.y,
        wasSelected: selected,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      onMove(annot.id, annot.x, annot.y, "start");
    },
    [editing, annot.id, annot.x, annot.y, selected, onSelect, onMove]
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag.current) return;
      const d = drag.current;
      onMove(
        annot.id,
        d.ox + (e.clientX - d.startX) / scale,
        d.oy + (e.clientY - d.startY) / scale,
        "move"
      );
    },
    [annot.id, scale, onMove]
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag.current) return;
      const d = drag.current;
      const moved =
        Math.abs(e.clientX - d.startX) > 3 || Math.abs(e.clientY - d.startY) > 3;
      drag.current = null;
      onMove(annot.id, annot.x, annot.y, "end");
      // Tap an already-selected box to edit it — the same "press again"
      // that opens a content text line. The Text tool still edits on the
      // first tap, since placing text is the only thing it's for.
      if (!moved && (tool === "text" || d.wasSelected)) onStartEdit(annot.id);
    },
    [annot.id, annot.x, annot.y, tool, onMove, onStartEdit]
  );

  // Gesture taken away mid-drag: put the box back where it started.
  const onPointerCancel = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    onMove(annot.id, annot.x, annot.y, "cancel");
  }, [annot.id, annot.x, annot.y, onMove]);

  // Text boxes only respond to the select & text tools — every other
  // tool draws straight through them.
  const interactive = editing || tool === "select" || tool === "text";
  return (
    <div
      ref={ref}
      className="pe-text-annot z-20"
      style={{
        left: annot.x * scale,
        top: annot.y * scale,
        fontSize: annot.size * scale,
        ...(fontCss ? { fontFamily: fontCss } : null),
        ...(annot.bold ? { fontWeight: 700 } : null),
        ...(annot.italic ? { fontStyle: "italic" as const } : null),
        // Rotate about the box's centre, matching the appearance matrix
        // the export writes (which turns about the BBox centre too).
        ...(annot.rotation
          ? { transform: `rotate(${annot.rotation}deg)`, transformOrigin: "center center" }
          : null),
        color: annot.color,
        textAlign: annot.align,
        opacity: annot.opacity,
        pointerEvents: interactive ? undefined : "none",
      }}
      data-selected={selected}
      data-draggable={!editing && interactive}
      contentEditable={editing ? "plaintext-only" : undefined}
      suppressContentEditableWarning
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!editing) onStartEdit(annot.id);
      }}
      onBlur={editing ? commit : undefined}
      onInput={editing ? report : undefined}
      onKeyDown={
        editing
          ? (e) => {
              e.stopPropagation();
              if (e.key === "Escape") ref.current?.blur();
            }
          : undefined
      }
    >
      {editing ? editSeed.current : annot.value}
    </div>
  );
});

// ──────────────────── drag helper + annotation views ────────────────────

/** Pointer-capture drag reporting cumulative page-space deltas. Ignores
 *  sub-3-px jitters so plain clicks never dispatch a move (and never
 *  pollute undo history).
 *
 *  Also reports `dragging` — true from the moment the gesture clears that
 *  jitter threshold until it ends. It flips on the first MOVE rather than
 *  on pointerdown so a plain click never flashes the things that watch
 *  it (dimension labels, at the time of writing). */
export function usePointerDrag(
  scale: number,
  onDrag: (dx: number, dy: number, phase: DragPhase) => void,
  onTap?: () => void
) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const onPointerDown = useCallback(
    (e: ReactPointerEvent<Element>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      start.current = { x: e.clientX, y: e.clientY, moved: false };
      e.currentTarget.setPointerCapture(e.pointerId);
      onDrag(0, 0, "start");
    },
    [onDrag]
  );
  const onPointerMove = useCallback(
    (e: ReactPointerEvent<Element>) => {
      const s = start.current;
      if (!s) return;
      if (!s.moved && Math.abs(e.clientX - s.x) <= 3 && Math.abs(e.clientY - s.y) <= 3) return;
      if (!s.moved) setDragging(true);
      s.moved = true;
      onDrag((e.clientX - s.x) / scaleRef.current, (e.clientY - s.y) / scaleRef.current, "move");
    },
    [onDrag]
  );
  const onPointerUp = useCallback(
    (e: ReactPointerEvent<Element>) => {
      const s = start.current;
      if (!s) return;
      start.current = null;
      setDragging(false);
      onDrag((e.clientX - s.x) / scaleRef.current, (e.clientY - s.y) / scaleRef.current, "end");
      if (!s.moved) onTap?.();
    },
    [onDrag, onTap]
  );
  // The browser reclaimed the gesture (touch scroll, OS gesture) — no
  // pointerup is coming. Roll the drag back instead of committing it,
  // and never fire onTap: nothing was tapped.
  const onPointerCancel = useCallback(() => {
    if (!start.current) return;
    start.current = null;
    setDragging(false);
    onDrag(0, 0, "cancel");
  }, [onDrag]);
  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, dragging };
}

/** One text-markup annotation (or live preview) as positioned divs —
 *  a transparent hit box per quad with the kind's decoration inside. */
export const MarkupQuadsView = memo(function MarkupQuadsView({
  id,
  kind,
  color,
  quads,
  scale,
  selectable,
  selected,
  onSelect,
}: {
  id?: string;
  kind: MarkupKind;
  color: string;
  quads: Quad[];
  scale: number;
  selectable: boolean;
  selected: boolean;
  onSelect?: (id: string, additive?: boolean) => void;
}) {
  return (
    <>
      {quads.map((q, i) => {
        const b = quadBounds(q);
        const h = b.height * scale;
        const bar = Math.max(1.5, h * 0.07);
        let deco: React.CSSProperties;
        switch (kind) {
          case "highlight":
            deco = { inset: 0, background: color, mixBlendMode: "multiply" };
            break;
          case "underline":
            deco = { left: 0, right: 0, bottom: h * 0.02, height: bar, background: color };
            break;
          case "strikeout":
            deco = { left: 0, right: 0, top: h * 0.46, height: bar, background: color };
            break;
          case "squiggly":
            deco = {
              left: 0,
              right: 0,
              bottom: 0,
              height: Math.max(2, 3 * scale),
              backgroundImage: wavyBg(color),
              backgroundSize: `${6 * scale}px ${3 * scale}px`,
              backgroundRepeat: "repeat-x",
            };
            break;
        }
        return (
          // No z-index here: DOM order already paints these above the
          // canvas, and a z-index would create a stacking context that
          // isolates the highlight's multiply blend from the page.
          <div
            key={id ? `${id}:${i}` : `preview:${i}`}
            className="pe-quad-hit"
            data-selected={selected}
            tabIndex={selectable && id && i === 0 ? 0 : undefined}
            role={selectable && id ? "button" : undefined}
            aria-label={selectable && id ? `${kind} annotation` : undefined}
            style={{
              left: b.x * scale,
              top: b.y * scale,
              width: b.width * scale,
              height: h,
              pointerEvents: selectable ? "auto" : "none",
              cursor: selectable ? "pointer" : undefined,
            }}
            onPointerDown={
              selectable && id && onSelect
                ? (e) => {
                    e.stopPropagation();
                    onSelect(id, e.shiftKey);
                  }
                : undefined
            }
            onKeyDown={
              selectable && id && onSelect
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelect(id, e.shiftKey);
                    }
                  }
                : undefined
            }
          >
            <div className="pe-quad-deco" style={deco} />
          </div>
        );
      })}
    </>
  );
});

/** Dashed selection box around an ink stroke / shape, in page units. */
export function SelectionBox({
  x,
  y,
  width,
  height,
  scale,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}) {
  const pad = 3 / scale;
  return (
    <rect
      x={x - pad}
      y={y - pad}
      width={width + pad * 2}
      height={height + pad * 2}
      fill="none"
      stroke="var(--color-accent)"
      strokeWidth={1.5 / scale}
      strokeDasharray={`${4 / scale} ${3 / scale}`}
      pointerEvents="none"
    />
  );
}

/** The rotate grip for a selection: a handle on a stalk above the
 *  selection's bounding box, dragged to turn it.
 *
 *  Reports the CHANGE in angle rather than an absolute one, so the caller
 *  can apply it with the same rotate-by-degrees operation the 90° buttons
 *  use — no separate "set the angle" path to keep consistent, and a drag
 *  composes with whatever rotation the selection already had. */
export function RotateHandle({
  box,
  scale,
  onRotateBy,
}: {
  /** Selection bounds in page points: x0, y0, x1, y1. */
  box: [number, number, number, number];
  scale: number;
  onRotateBy: (deg: number, phase: DragPhase) => void;
}) {
  const cx = (box[0] + box[2]) / 2;
  const cy = (box[1] + box[3]) / 2;
  const stalk = 22 / scale;
  const hy = box[1] - stalk;
  const last = useRef<number | null>(null);

  const angleAt = (e: ReactPointerEvent<Element>): number => {
    const svg = (e.currentTarget as SVGElement).ownerSVGElement;
    const rect = svg?.getBoundingClientRect();
    if (!rect) return 0;
    // Pointer position in page points, then the bearing from the centre.
    const px = ((e.clientX - rect.left) / rect.width) * (svg!.viewBox.baseVal.width || 1);
    const py = ((e.clientY - rect.top) / rect.height) * (svg!.viewBox.baseVal.height || 1);
    return (Math.atan2(py - cy, px - cx) * 180) / Math.PI;
  };

  return (
    <g>
      <line
        x1={cx}
        y1={box[1]}
        x2={cx}
        y2={hy}
        stroke="var(--color-accent)"
        strokeWidth={1 / scale}
        pointerEvents="none"
      />
      <circle
        cx={cx}
        cy={hy}
        r={(9) / scale}
        fill="transparent"
        style={{ pointerEvents: "all", cursor: "grab", touchAction: "none" }}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          last.current = angleAt(e);
          onRotateBy(0, "start");
        }}
        onPointerMove={(e) => {
          if (last.current === null) return;
          const now = angleAt(e);
          // Shift snaps the TOTAL angle to 15°, so the step sent is
          // whatever gets there from where the selection already is.
          let delta = now - last.current;
          if (delta > 180) delta -= 360;
          if (delta < -180) delta += 360;
          if (e.shiftKey) {
            const snapped = Math.round(delta / 15) * 15;
            if (snapped === 0) return;
            delta = snapped;
          }
          last.current = now;
          onRotateBy(delta, "move");
        }}
        onPointerUp={() => {
          if (last.current === null) return;
          last.current = null;
          onRotateBy(0, "end");
        }}
        onPointerCancel={() => {
          last.current = null;
          onRotateBy(0, "cancel");
        }}
      />
      <circle
        cx={cx}
        cy={hy}
        r={4.5 / scale}
        fill="var(--color-bg)"
        stroke="var(--color-accent)"
        strokeWidth={1.5 / scale}
        pointerEvents="none"
      />
    </g>
  );
}

export const InkView = memo(function InkView({
  a,
  scale,
  selectable,
  selected,
  onSelect,
  onDrag,
}: {
  a: InkAnnotation;
  scale: number;
  selectable: boolean;
  selected: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}) {
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(a.id, dx, dy, phase), [a.id, onDrag])
  );
  const d = pointsToPath(a.points);
  let box: { x: number; y: number; width: number; height: number } | null = null;
  if (selected) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i + 1 < a.points.length; i += 2) {
      minX = Math.min(minX, a.points[i]!);
      maxX = Math.max(maxX, a.points[i]!);
      minY = Math.min(minY, a.points[i + 1]!);
      maxY = Math.max(maxY, a.points[i + 1]!);
    }
    box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={a.color}
        strokeWidth={a.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        pointerEvents="none"
      />
      {selectable && (
        // Invisible fat twin so thin strokes are still grabbable.
        <path
          d={d}
          fill="none"
          stroke="transparent"
          strokeWidth={Math.max(a.strokeWidth + 8, 12)}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: "stroke", cursor: "move", touchAction: "none" }}
          onPointerDown={(e) => {
            onSelect(a.id, e.shiftKey);
            drag.onPointerDown(e);
          }}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
          onPointerCancel={drag.onPointerCancel}
        />
      )}
      {box && <SelectionBox {...box} scale={scale} />}
    </g>
  );
});

/** One line-end decoration at (x, y), pointing along `ang` (radians,
 *  the outward direction of the line at that end). */
export function LineHead({
  x,
  y,
  ang,
  style,
  color,
  strokeWidth,
}: {
  x: number;
  y: number;
  ang: number;
  style: HeadStyle;
  color: string;
  strokeWidth: number;
}) {
  if (style === "none") return null;
  const len = Math.max(9, strokeWidth * 3.5);
  const r = Math.max(4, strokeWidth * 2.5);
  const pt = (t: number): [number, number] => [
    x - len * Math.cos(ang + t),
    y - len * Math.sin(ang + t),
  ];
  switch (style) {
    case "arrow": {
      const [ax, ay] = pt(0.45);
      const [bx, by] = pt(-0.45);
      return <path d={`M ${x} ${y} L ${ax} ${ay} L ${bx} ${by} Z`} fill={color} stroke="none" pointerEvents="none" />;
    }
    case "open": {
      const [ax, ay] = pt(0.45);
      const [bx, by] = pt(-0.45);
      return (
        <path
          d={`M ${ax} ${ay} L ${x} ${y} L ${bx} ${by}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents="none"
        />
      );
    }
    case "circle":
      return <circle cx={x} cy={y} r={r} fill={color} pointerEvents="none" />;
    case "square":
      return <rect x={x - r} y={y - r} width={r * 2} height={r * 2} fill={color} pointerEvents="none" />;
    case "diamond":
      return (
        <path
          d={`M ${x} ${y - r * 1.3} L ${x + r * 1.3} ${y} L ${x} ${y + r * 1.3} L ${x - r * 1.3} ${y} Z`}
          fill={color}
          pointerEvents="none"
        />
      );
  }
}

export const ShapeView = memo(function ShapeView({
  a,
  scale,
  selectable,
  selected,
  onSelect,
  onDrag,
  onResize,
  dims,
}: {
  a: ShapeAnnotation;
  scale: number;
  selectable: boolean;
  selected: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
  onResize: (id: string, end: 1 | 2, dx: number, dy: number, phase: DragPhase) => void;
  /** Force the dimension labels on. Set by the live creation draft,
   *  which is a resize by another name — you are sizing the thing, so
   *  the size is exactly what you want to see. */
  dims?: boolean;
}) {
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(a.id, dx, dy, phase), [a.id, onDrag])
  );
  const resize1 = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onResize(a.id, 1, dx, dy, phase), [a.id, onResize])
  );
  const resize2 = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onResize(a.id, 2, dx, dy, phase), [a.id, onResize])
  );
  const coarse = useCoarsePointer();

  const x = Math.min(a.x1, a.x2);
  const y = Math.min(a.y1, a.y2);
  const w = Math.abs(a.x2 - a.x1);
  const h = Math.abs(a.y2 - a.y1);
  // A line's dimension is its length; a box's is width and height.
  const showDims = dims || resize1.dragging || resize2.dragging;
  const isLine = a.shape === "line" || a.shape === "arrow";

  function geometry(hit: boolean): React.ReactNode {
    const props = hit
      ? {
          stroke: "transparent",
          strokeWidth: Math.max(a.strokeWidth + 8, 12),
          // The hit twin is ALWAYS filled, even when the shape isn't:
          // clicking inside an outline-only rectangle has to select it
          // (a stroke-only hit area makes plain and shift+click feel
          // broken — you have to land on the 12pt band). Harmless on
          // line/arrow, which have no interior.
          fill: "transparent",
          style: {
            pointerEvents: "visiblePainted",
            cursor: "move",
            touchAction: "none",
          } as React.CSSProperties,
          onPointerDown: (e: ReactPointerEvent<Element>) => {
            onSelect(a.id, e.shiftKey);
            drag.onPointerDown(e);
          },
          onPointerMove: drag.onPointerMove,
          onPointerUp: drag.onPointerUp,
          onPointerCancel: drag.onPointerCancel,
        }
      : {
          stroke: a.color,
          strokeWidth: a.strokeWidth,
          fill: a.fill ?? "none",
          strokeDasharray: dashArray(a.dash, a.strokeWidth),
          pointerEvents: "none" as const,
        };
    switch (a.shape) {
      case "rect":
        return <rect x={x} y={y} width={w} height={h} {...props} />;
      case "ellipse":
        return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...props} />;
      case "line":
      case "arrow": {
        const heads = shapeHeads(a);
        const ang = Math.atan2(a.y2 - a.y1, a.x2 - a.x1);
        return (
          <g {...(hit ? {} : { pointerEvents: "none" })}>
            <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} {...props} />
            {!hit && (
              <>
                <LineHead
                  x={a.x1}
                  y={a.y1}
                  ang={ang + Math.PI}
                  style={heads.start}
                  color={a.color}
                  strokeWidth={a.strokeWidth}
                />
                <LineHead
                  x={a.x2}
                  y={a.y2}
                  ang={ang}
                  style={heads.end}
                  color={a.color}
                  strokeWidth={a.strokeWidth}
                />
              </>
            )}
          </g>
        );
      }
    }
  }

  // A rect or ellipse rotates about its own centre — the same centre the
  // exported appearance matrix turns about, so the overlay and the file
  // agree. Lines and arrows carry no angle: rotating one moved its
  // endpoints, which is already in the geometry.
  const spin =
    a.rotation && a.shape !== "line" && a.shape !== "arrow"
      ? `rotate(${a.rotation} ${x + w / 2} ${y + h / 2})`
      : undefined;

  return (
    <g transform={spin}>
      {geometry(false)}
      {selectable && geometry(true)}
      {selected && <SelectionBox x={x} y={y} width={w} height={h} scale={scale} />}
      {showDims &&
        (isLine ? (
          <SvgLineDim x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} scale={scale} />
        ) : (
          <SvgBoxDims x={x} y={y} width={w} height={h} scale={scale} />
        ))}
      {selected && selectable && (
        <>
          {(
            [
              [a.x1, a.y1, resize1],
              [a.x2, a.y2, resize2],
            ] as const
          ).map(([hx, hy, handle], i) => (
            <g key={i}>
              {/* Transparent hit twin: a fingertip needs far more than
                  the 5-px dot the eye needs, and the visible handle
                  can't grow without swamping short lines. */}
              <circle
                cx={hx}
                cy={hy}
                r={(coarse ? 14 : 7) / scale}
                fill="transparent"
                style={{ pointerEvents: "all", cursor: "nwse-resize", touchAction: "none" }}
                onPointerDown={handle.onPointerDown}
                onPointerMove={handle.onPointerMove}
                onPointerUp={handle.onPointerUp}
                onPointerCancel={handle.onPointerCancel}
              />
              <circle
                cx={hx}
                cy={hy}
                r={5 / scale}
                fill="var(--color-bg)"
                stroke="var(--color-accent)"
                strokeWidth={1.5 / scale}
                pointerEvents="none"
              />
            </g>
          ))}
        </>
      )}
    </g>
  );
});

export const NoteView = memo(function NoteView({
  annot,
  scale,
  tool,
  selected,
  editing,
  onSelect,
  onStartEdit,
  onCommitNote,
  onDrag,
}: {
  annot: NoteAnnotation;
  scale: number;
  tool: Tool;
  selected: boolean;
  editing: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onStartEdit: (id: string) => void;
  onCommitNote: (id: string, value: string) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}) {
  // Sampled at press time — the press selects, so reading `selected` at
  // tap time would make every first tap open the note.
  const wasSelected = useRef(false);
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(annot.id, dx, dy, phase), [annot.id, onDrag]),
    useCallback(() => {
      if (wasSelected.current && !editing) onStartEdit(annot.id);
    }, [annot.id, editing, onStartEdit])
  );
  const interactive = tool === "select" || editing;

  // The textarea is uncontrolled; blur commits it. But opening another
  // note's editor unmounts this one without a blur event, so mirror the
  // draft into a ref and commit from the effect cleanup. Only commit a
  // *changed* draft: StrictMode's mount→cleanup→remount cycle runs the
  // cleanup with a pristine draft, and committing that "" would delete
  // the note the instant it's created.
  const draft = useRef(annot.value);
  const valueRef = useRef(annot.value);
  valueRef.current = annot.value;
  const commitRef = useRef(onCommitNote);
  commitRef.current = onCommitNote;
  useEffect(() => {
    if (!editing) return;
    draft.current = valueRef.current;
    const id = annot.id;
    return () => {
      if (draft.current !== valueRef.current) commitRef.current(id, draft.current);
    };
  }, [editing, annot.id]);
  return (
    <div
      className="pe-note z-20"
      data-selected={selected}
      tabIndex={tool === "select" ? 0 : undefined}
      role={tool === "select" ? "button" : undefined}
      aria-label={tool === "select" ? `Sticky note: ${annot.value || "empty"}` : undefined}
      onKeyDown={
        tool === "select" && !editing
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onSelect(annot.id, e.shiftKey);
              }
            }
          : undefined
      }
      title={editing ? undefined : annot.value}
      style={{
        left: annot.x * scale,
        top: annot.y * scale,
        width: NOTE_SIZE * scale,
        height: NOTE_SIZE * scale,
        fontSize: NOTE_SIZE * scale * 0.9,
        color: annot.color,
        pointerEvents: interactive ? "auto" : "none",
      }}
      onPointerDown={(e) => {
        wasSelected.current = selected;
        onSelect(annot.id, e.shiftKey);
        drag.onPointerDown(e);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!editing) onStartEdit(annot.id);
      }}
    >
      <i className="ri-sticky-note-fill" aria-hidden />
      {editing && (
        <textarea
          className="pe-note-editor"
          defaultValue={annot.value}
          autoFocus
          placeholder="Note…"
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            draft.current = e.currentTarget.value;
          }}
          onBlur={(e) => onCommitNote(annot.id, e.currentTarget.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Escape") e.currentTarget.blur();
          }}
        />
      )}
    </div>
  );
});

/** Pending-redaction boxes: translucent black with a warning outline so
 *  what's underneath stays reviewable until export applies them. */
export const RedactView = memo(function RedactView({
  a,
  scale,
  selectable,
  selected,
  onSelect,
  onDrag,
}: {
  a: RedactAnnotation;
  scale: number;
  selectable: boolean;
  selected: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}) {
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(a.id, dx, dy, phase), [a.id, onDrag])
  );
  return (
    <>
      {a.quads.map((q, i) => {
        const b = quadBounds(q);
        return (
          <div
            key={`${a.id}:${i}`}
            className="pe-redact z-10"
            data-selected={selected}
            style={{
              left: b.x * scale,
              top: b.y * scale,
              width: b.width * scale,
              height: b.height * scale,
              pointerEvents: selectable ? "auto" : "none",
              cursor: selectable ? "move" : undefined,
            }}
            onPointerDown={(e) => {
              onSelect(a.id, e.shiftKey);
              drag.onPointerDown(e);
            }}
            onPointerMove={drag.onPointerMove}
            onPointerUp={drag.onPointerUp}
            onPointerCancel={drag.onPointerCancel}
          />
        );
      })}
    </>
  );
});

export const StampView = memo(function StampView({
  a,
  scale,
  tool,
  selected,
  onSelect,
  onDrag,
  onResize,
}: {
  a: StampAnnotation;
  scale: number;
  tool: Tool;
  selected: boolean;
  onSelect: (id: string, additive?: boolean) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
  onResize: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}) {
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(a.id, dx, dy, phase), [a.id, onDrag])
  );
  const resize = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onResize(a.id, dx, dy, phase), [a.id, onResize])
  );
  const interactive = tool === "select";
  return (
    <div
      className="pe-stamp z-20"
      data-selected={selected}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      aria-label={interactive ? "Stamp annotation" : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onSelect(a.id, e.shiftKey);
              }
            }
          : undefined
      }
      style={{
        left: a.x * scale,
        top: a.y * scale,
        width: a.width * scale,
        height: a.height * scale,
        ...(a.rotation
          ? { transform: `rotate(${a.rotation}deg)`, transformOrigin: "center center" }
          : null),
        pointerEvents: interactive ? "auto" : "none",
      }}
      onPointerDown={(e) => {
        onSelect(a.id, e.shiftKey);
        drag.onPointerDown(e);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      <img src={a.dataUrl} alt="" draggable={false} className="h-full w-full select-none" />
      {resize.dragging && (
        <HtmlBoxDims
          width={a.width}
          height={a.height}
          scale={scale}
          originX={a.x}
          originY={a.y}
        />
      )}
      {selected && interactive && (
        <div
          className="pe-stamp-handle"
          title="Resize"
          onPointerDown={resize.onPointerDown}
          onPointerMove={resize.onPointerMove}
          onPointerUp={resize.onPointerUp}
          onPointerCancel={resize.onPointerCancel}
        />
      )}
    </div>
  );
});

/** A clickable link region. In select mode a click follows it (external
 *  URLs open in a new tab after a confirm; internal ones jump); the
 *  outline only shows on hover, when selected, or in link mode. */
export const LinkView = memo(function LinkView({
  a,
  scale,
  tool,
  selected,
  onSelect,
  onFollow,
  onEdit,
  onDrag,
}: {
  a: LinkAnnotation;
  scale: number;
  tool: Tool;
  selected: boolean;
  onSelect: (id: string | null, additive?: boolean) => void;
  onFollow: (a: LinkAnnotation) => void;
  onEdit: (a: LinkAnnotation) => void;
  onDrag: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}) {
  const interactive = tool === "select" || tool === "link";
  const drag = usePointerDrag(
    scale,
    useCallback((dx, dy, phase) => onDrag(a.id, dx, dy, phase), [a.id, onDrag])
  );
  const [x0, y0, x1, y1] = a.rect;
  return (
    <div
      className="pe-link"
      data-selected={selected || undefined}
      data-linkmode={tool === "link" || undefined}
      title={
        a.url
          ? `Link: ${a.url}${tool === "select" ? " — click to open (Link tool to edit)" : " — drag to move, double-click to edit"}`
          : `Link → page ${(a.page ?? 0) + 1}${tool === "select" ? " — click to jump (Link tool to edit)" : " — drag to move, double-click to edit"}`
      }
      style={{
        left: x0 * scale,
        top: y0 * scale,
        width: Math.max(2, x1 - x0) * scale,
        height: Math.max(2, y1 - y0) * scale,
        pointerEvents: interactive ? "all" : "none",
        cursor: tool === "select" ? "pointer" : "move",
      }}
      onPointerDown={(e) => {
        if (!interactive) return;
        e.stopPropagation();
        onSelect(a.id, e.shiftKey);
        if (tool === "link") drag.onPointerDown(e);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
      onClick={(e) => {
        if (tool !== "select" || e.detail > 1 || e.shiftKey) return;
        e.stopPropagation();
        onFollow(a);
      }}
      onDoubleClick={(e) => {
        // Only in link mode: in select mode the first click already
        // followed the link (and may have scrolled the page away).
        if (tool !== "link") return;
        e.stopPropagation();
        onEdit(a);
      }}
    />
  );
});
